import { useEffect, useState, useMemo, useRef } from 'react';
import { useSelector } from 'react-redux';
import { format } from "date-fns";
import { 
    useTheme, 
    useMediaQuery, 
    Box, 
    Typography, 
    CircularProgress,
    FormControl, 
    MenuItem, 
    InputLabel, 
    Select,       
} from '@mui/material';
import useAuthCheck from 'hooks/useAuthCheck';
import { useDateRangeContext } from 'contexts/DateRangeContext';
import { LAYOUT_HEIGHT, DIVERT_RETURN_STATUS, EXCLUDED_FLIGHT_STATUS } from 'state/constants';
import { removeItemByValueImmutable, filterFlights } from 'state/helpers';
import { countValuesByField } from './utils';
import Header from 'components/Header';
import ScrollToTop from 'components/ScrollToTop';
import FlexBetween from 'components/FlexBetween';
import FilterButton from 'components/FilterButton';
import FlightFilterDialog from 'components/FlightFilterDialog';
import ChartDialog from 'components/chartNivo/ChartDialog';
import SortBarMultiLinesChart from 'components/chartNivo/SortBarMultiLinesChart';
import { boxStyles as styles } from 'components/CustomStyles';


const viewOptions = [
  { value: "tatDiffByFlightDate", label: "by Date" },
  { value: "tatDiffByDep", label: "by Port" },
  { value: "tatDiffBySector", label: "by Sector" },
  { value: "tatDiffBySectorType", label: "by Sector Type" },
  { value: "tatDiffByFlightType", label: "by Flight Type" },
  { value: "tatDiffByFlightReg", label: "by Flight Reg" },
  { value: "tatDiffByACType", label: "by AC Type" },
  { value: "tatDiffByCountry", label: "by Country" },
  { value: "tatDiffByFlightNo", label: "by Flight No" },
];

const TATDiffChart = () => {   
    useAuthCheck();
    const timeMode = useSelector((state) => state.timemode);
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

    const { startDate, endDate } = useDateRangeContext();

    const scrollRef = useRef(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [isFilterDialogOpen, setIsFilterDialogOpen] = useState(false);
    const [filters, setFilters] = useState(null);
    const [chartOpen, setChartOpen] = useState(false);
    
    const [compareBy, setCompareBy] = useState('actualschedule');
    const [view, setView] = useState('tatDiffByFlightDate');
    const [flights, setFlights] = useState([]); 
    const [data, setData] = useState({
        tatDiffByFlightDate: [],
        tatDiffByDep: [],
        tatDiffBySector: [],
        tatDiffBySectorType: [],
        tatDiffByFlightType: [],
        tatDiffByFlightReg: [],
        tatDiffByACType: [],
        tatDiffByCountry: [],
        tatDiffByFlightNo: [],
    });  

    const flightsData = useSelector((state) => state.flights); 

    useEffect(() => {        
        const handleFlights = async () => {
            try {           
                setLoading(true); 
                const processedFlights = removeItemByValueImmutable(flightsData, 'flightStatus', EXCLUDED_FLIGHT_STATUS)
                    .filter(flight => 
                        !DIVERT_RETURN_STATUS.includes(flight.flightStatus)
                    );
                setFlights(processedFlights);                
            } catch (error) {
                setError(error);
            } finally {
                setLoading(false);
            }
        };        
        handleFlights();      
    }, [flightsData]);
                
    const filteredFlights = useMemo(() => {
        return filterFlights({ flights, filters, timeMode });
    }, [flights, filters, timeMode]);    

    useEffect(() => {        
        const results = {
            tatDiffByFlightDate: countValuesByField(filteredFlights, compareBy === 'actualschedule' ? 'tatDiff' : 'actualEstimatedTatDiff', timeMode === 'UTC' ? 'flightDate' : 'flightDateLocalBase'),
            tatDiffByDep: countValuesByField(filteredFlights, compareBy === 'actualschedule' ? 'tatDiff' : 'actualEstimatedTatDiff', 'dep'),
            tatDiffBySector: countValuesByField(filteredFlights, compareBy === 'actualschedule' ? 'tatDiff' : 'actualEstimatedTatDiff', 'sector'),
            tatDiffBySectorType: countValuesByField(filteredFlights, compareBy === 'actualschedule' ? 'tatDiff' : 'actualEstimatedTatDiff', 'sectorType'),
            tatDiffByFlightType: countValuesByField(filteredFlights, compareBy === 'actualschedule' ? 'tatDiff' : 'actualEstimatedTatDiff', 'flightType'),
            tatDiffByFlightReg: countValuesByField(filteredFlights, compareBy === 'actualschedule' ? 'tatDiff' : 'actualEstimatedTatDiff', 'flightReg'),
            tatDiffByACType: countValuesByField(filteredFlights, compareBy === 'actualschedule' ? 'tatDiff' : 'actualEstimatedTatDiff', 'acType'),
            tatDiffByCountry: countValuesByField(filteredFlights, compareBy === 'actualschedule' ? 'tatDiff' : 'actualEstimatedTatDiff', 'depCountryName'),
            tatDiffByFlightNo: countValuesByField(filteredFlights, compareBy === 'actualschedule' ? 'tatDiff' : 'actualEstimatedTatDiff', 'flightNo'),
        };            
        setData(results); 
    }, [timeMode, filteredFlights, compareBy]);
    
    const handleFilterOpen = () => setIsFilterDialogOpen(true);
    const handleFilterClose = () => setIsFilterDialogOpen(false);
    
    const handleFilterApply = (filterValues) => {
        setFilters(filterValues); 
    };
        
    const {
        acTypeOptions,
        flightRegOptions,
        sectorTypeOptions,
        departureTypeOptions,
        arrivalTypeOptions,
    } = useMemo(() => {
        if (!Array.isArray(flights)) {
            return {
                acTypeOptions: [],
                flightRegOptions: [],
                sectorTypeOptions: [],
                departureTypeOptions: [],
                arrivalTypeOptions: [],
            };
        }

        const sorted = (key) =>
            [...new Set(flights.map((f) => f[key]).filter(Boolean))].sort();

        return {
            acTypeOptions: sorted("acType"),
            flightRegOptions: sorted("flightReg"),
            sectorTypeOptions: sorted("sectorType"),
            departureTypeOptions: sorted("departureType"),
            arrivalTypeOptions: sorted("arrivalType"),
        };
    }, [flights]);
    
    const handleOpen = () => {
        setChartOpen(true);
    };

    const handleClose = () => {
        setChartOpen(false);
    };

    const period = `${startDate ? format(startDate, 'ddMMMyy') : ''} - ${endDate ? format(endDate, 'ddMMMyy') : ''}`;
    const defaultTitle = `TAT Diff ${period}`;

    const chartMap = {
        tatDiffByFlightDate: { component: SortBarMultiLinesChart, props: { title: defaultTitle, bottomLegend: "Date", leftLegend: "Minutes", digit: 1, barTranslateXOffSet: 35, lineTranslateXOffSet: -10 } },
        tatDiffByDep: { component: SortBarMultiLinesChart, props: { title: defaultTitle, bottomLegend: "Port", leftLegend: "Minutes", digit: 1, barTranslateXOffSet: 35, lineTranslateXOffSet: -10 } },
        tatDiffBySector: { component: SortBarMultiLinesChart, props: { title: defaultTitle, bottomLegend: "Sector", leftLegend: "Minutes", digit: 1, bottomOffSet: 10, bottomLegendOffSet: 10, barTranslateXOffSet: 35, lineTranslateXOffSet: -10 } },
        tatDiffBySectorType: { component: SortBarMultiLinesChart, props: { title: defaultTitle, bottomLegend: "Sector Type", leftLegend: "Minutes", isHorizontalBottomLegend: true, digit: 1, bottomOffSet: -30, bottomLegendOffSet: -30, barTranslateXOffSet: 35, lineTranslateXOffSet: -10 } },
        tatDiffByFlightType: { component: SortBarMultiLinesChart, props: { title: defaultTitle, bottomLegend: "Flight Type", leftLegend: "Minutes", isHorizontalBottomLegend: !isMobile ? true : false, digit: 1, bottomOffSet: !isMobile ? -30 : 50, bottomLegendOffSet: !isMobile ? -30 : 50, barTranslateXOffSet: 35, lineTranslateXOffSet: -10 } },
        tatDiffByFlightReg: { component: SortBarMultiLinesChart, props: { title: defaultTitle, bottomLegend: "Flight Reg", leftLegend: "Minutes", digit: 1, bottomOffSet: 5, bottomLegendOffSet: 5, barTranslateXOffSet: 35, lineTranslateXOffSet: -10 } },
        tatDiffByACType: { component: SortBarMultiLinesChart, props: { title: defaultTitle, bottomLegend: "AC Type", leftLegend: "Minutes", digit: 1, barTranslateXOffSet: 35, lineTranslateXOffSet: -10 } },
        tatDiffByCountry: { component: SortBarMultiLinesChart, props: { title: defaultTitle, bottomLegend: "Country", leftLegend: "Minutes", digit: 0, bottomOffSet: isMobile ? 55 : 45, bottomLegendOffSet: isMobile ? 45 : 45, barTranslateXOffSet: 35, lineTranslateXOffSet: -10 } },
        tatDiffByFlightNo: { component: SortBarMultiLinesChart, props: { title: defaultTitle, bottomLegend: "Flight No", leftLegend: "Minutes", digit: 1, bottomOffSet: 5, bottomLegendOffSet: 5, barTranslateXOffSet: 35, lineTranslateXOffSet: -10 } },
    };
    
    const renderChart = (view, customStyles) => {
        const chart = chartMap[view];
        return chart ? (
          <Box sx={{ ...styles, ...customStyles }}>
            <chart.component data={data[view]} {...chart.props} />
          </Box>
        ) : (
          <Box sx={{ ...styles, ...customStyles }}>                                
            <Typography>No charts available</Typography>
          </Box>
        );
    };


    return (
        <Box className="main-container-box">                
            <FlexBetween>
                <Header 
                    title="TAT Diff" 
                    subtitle={`${loading ? 'Loading...' : 'List of Charts'}`}
                />     
                {!loading && (
                    <Box display="flex" alignItems="center" gap={1}>
                        <FormControl
                            size="small"
                            sx={{ width: { xs: '6rem', sm: '10rem' } }}
                        >
                            <InputLabel>Compare by</InputLabel>
                            <Select
                                label="Compare by"
                                value={compareBy}
                                onChange={(e) => {setCompareBy(e.target.value)}}
                            >
                                <MenuItem value="actualschedule">Actual - Schedule</MenuItem>
                                <MenuItem value="actualestimated">Actual - Estimated</MenuItem>
                                
                            </Select>
                        </FormControl>
                        <FormControl
                            size="small"
                            sx={{ width: { xs: '6rem', sm: '9rem' } }}
                        >
                            <InputLabel id="tat-diff-view-label">View</InputLabel>
                            <Select
                                labelId="tat-diff-view-label"
                                label="View"
                                value={view}
                                onChange={(e) => setView(e.target.value)}
                            >
                                {viewOptions.map(opt => (
                                    <MenuItem key={opt.value} value={opt.value}>
                                        {opt.label}
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>

                        <FilterButton
                            filters={filters}
                            onClick={handleFilterOpen}
                        />
                    </Box>
                )}                    
            </FlexBetween>               
            {loading ? (
                <Box sx={{ ...styles, height: LAYOUT_HEIGHT }}>
                    <CircularProgress />
                </Box>
            ) : error ? (
                <Box sx={{ ...styles, height: LAYOUT_HEIGHT }}>
                    <Typography color="error">Error: {error?.message || ''}</Typography>
                </Box>
            ) : (
                <Box
                    display="grid"                        
                    gridTemplateColumns="repeat(12, minmax(0, 1fr))"                
                >
                    <Box
                        ref={scrollRef}
                        height={LAYOUT_HEIGHT}  
                        display="flex"
                        flexDirection="column"
                        gap={2}
                        sx={{ gridColumn: "span 12", overflowY: 'auto' }}
                        onDoubleClick={handleOpen}
                    >
                        {renderChart(view, { height: LAYOUT_HEIGHT })}                         
                    </Box>

                    <ScrollToTop scrollRef={scrollRef} />
                </Box>
            )}
              
            {isFilterDialogOpen &&
                <FlightFilterDialog
                    open={isFilterDialogOpen}
                    onClose={handleFilterClose}
                    onApply={handleFilterApply}
                    acTypeOptions={acTypeOptions} 
                    flightRegOptions={flightRegOptions}
                    sectorTypeOptions={sectorTypeOptions}
                    departureTypeOptions={departureTypeOptions}
                    arrivalTypeOptions={arrivalTypeOptions}
                    currentFilters={filters}
                />
            }

            {chartOpen && (
                <ChartDialog
                    open={chartOpen}
                    onClose={handleClose}
                    chartComponent={renderChart(view, {})}
                />
            )}
        </Box>
    );
};

export default TATDiffChart;
